import { useEffect, useRef } from 'react';
import { isIdleExpired, isOtpSession, recordAddinActivity } from '../../core/auth/idleSession';
import { loginService } from '../../core/auth/loginService';
import { OTP_IDLE_TIMEOUT_MS } from '../../utils/config';

// Interactions in the taskpane that count as using the add-in.
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

// Check often enough that the logout lands close to the deadline, without
// polling hard for the production 30-minute value.
const CHECK_INTERVAL_MS = Math.max(1000, Math.min(15000, Math.floor(OTP_IDLE_TIMEOUT_MS / 6)));

/**
 * Taskpane side of the OTP idle timeout (see core/auth/idleSession): records
 * taskpane interactions as activity and, once an OTP session has gone idle,
 * calls the server logout and invokes `onIdleLogout` so the app can show the
 * login screen. Inert for normal (non-OTP) sessions.
 */
export function useIdleLogout(onIdleLogout: () => void, enabled = true): void {
  const onIdleLogoutRef = useRef(onIdleLogout);
  onIdleLogoutRef.current = onIdleLogout;

  useEffect(() => {
    if (!enabled) return undefined;

    let checking = false;
    const checkIdle = async () => {
      if (checking || !isOtpSession() || !isIdleExpired()) return;
      checking = true;
      try {
        if (await loginService.enforceIdleTimeout()) {
          onIdleLogoutRef.current();
        }
      } finally {
        checking = false;
      }
    };

    const handleActivity = () => {
      // An interaction after the deadline must log out, not extend the session.
      if (isIdleExpired()) {
        void checkIdle();
      } else {
        recordAddinActivity();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void checkIdle();
    };

    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, handleActivity, { capture: true, passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);
    const intervalId = window.setInterval(() => { void checkIdle(); }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, handleActivity, { capture: true }));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(intervalId);
    };
  }, [enabled]);
}
